import { useNewItems } from '../../../lib/sectionNewItems'
import { NewItemDot } from '../NewItemDot'
import { HeaderRefreshButton } from '../ListSelection'
/**
 * Citas: una sola página, dos maneras de mirar los mismos datos.
 *
 * No reutiliza el calendario de la ficha de un servicio, y no por descuido:
 * aquél pinta la DISPONIBILIDAD de un servicio concreto —los huecos que quedan
 * libres— y para eso necesita su duración, su horario y su recurso. Aquí no hay
 * un servicio del que hablar, así que lo que se pinta es lo contrario: lo que ya
 * está ocupado, venga de donde venga.
 *
 * El mes se arma con aritmética UTC sobre las fechas ya contadas en la zona del
 * negocio. Construirlas en la del navegador metería un desfase de horas que en
 * los extremos del mes cambia el día — el dueño en Madrid vería la agenda de su
 * local de Los Ángeles corrida.
 */
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { useEffect, useMemo, useState } from 'react'
import { IconCalendarMonth, IconCalendarPlus, IconCalendarShare, IconChevronLeft, IconChevronRight, IconListCheck, IconPlus, IconUser, IconUsers } from '@tabler/icons-react'

import { SectionRefresh } from '../../SectionRefresh'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useToast } from '../../Toast'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { BookingDetail } from './BookingDetail'
import { ArtistFace, BookingsTable, GroupBookingDialog } from './BookingsTable'
import { NewBookingDialog } from './NewBookingDialog'
import { CalendarSyncDialog } from './CalendarSyncDialog'
import { BOOKING_STATUS_ICON, BOOKING_STATUS_KEY, BOOKING_STATUS_TONE, OVERDUE_HINT_KEY, allDayCount, bookingError, groupBookings, needsOutcome, peopleLabel } from './bookingShared'
import { useBookingsData, todayIn } from './useBookingsData'
import { markBookingChangeSeen, updateBooking, type Booking } from '../../../lib/api'

/**
 * Las iniciales de los días, sacadas de `Intl` y no escritas a mano.
 *
 * Estaban como `['D','L','M','X','J','V','S']`, que es el castellano: en inglés
 * la semana empieza igual en domingo pero se lee «S M T W T F S», y una lista
 * fija sólo puede estar bien en un idioma. Se toma el 4 de enero de 1970 —un
 * domingo— y se avanza día a día, que es la forma de pedirle a `Intl` los siete
 * nombres sin depender de la fecha de hoy.
 */
function weekdayInitials(): string[] {
  const format = new Intl.DateTimeFormat(activeLanguage(), { timeZone: 'UTC', weekday: 'narrow' })
  return Array.from({ length: 7 }, (_unused, index) =>
    format.format(new Date(Date.UTC(1970, 0, 4 + index))))
}

const pad2 = (n: number) => (n < 10 ? '0' : '') + n
const keyOf = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`
/**
 * En qué columna cae el día 1. Domingo = 0.
 *
 * `getUTCDay()` ya devuelve domingo como cero, así que no hay que corregir nada:
 * la semana de este calendario empieza en domingo, como en México y buena parte
 * de América —que es donde está el negocio que lo usa—.
 */
const firstColumn = (y: number, m: number) => new Date(Date.UTC(y, m, 1)).getUTCDay()
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate()

function monthLabel(y: number, m: number): string {
  const text = new Intl.DateTimeFormat(activeLanguage(), { timeZone: 'UTC', month: 'long', year: 'numeric' })
    .format(new Date(Date.UTC(y, m, 1)))
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Las citas de un día de otro mes: sólo la hora y el nombre.
 *
 * Deliberadamente más pobre que la pastilla del mes en curso —sin servicio, sin
 * estado, sin desplegar grupos—: son días de contexto, y darles el mismo peso
 * que a los del mes que se está mirando haría que la vista dejara de tener un
 * mes protagonista.
 */
function MiniDayList({ list, onOpen }: {
  list: Booking[]
  onOpen?: ((record: string) => void) | undefined
}) {
  const { t } = useI18n()
  const vivas = list.filter((row) => row.status !== 'cancelled')
  if (vivas.length === 0) return null
  return (
    <div className="dash-agenda-mini">
      {vivas.slice(0, 3).map((row) => (
        <button
          key={row.id}
          type="button"
          className="dash-agenda-mini-chip"
          onClick={() => onOpen?.(row.id)}
          title={`${row.customerName || t('bookings.noName')} · ${row.offerTitle}`}
        >
          {new Intl.DateTimeFormat(activeLanguage(), { timeZone: row.timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(row.startsAt))}
          <span>{row.customerName || row.offerTitle}</span>
        </button>
      ))}
      {vivas.length > 3 ? <span className="dash-agenda-mini-more">+{vivas.length - 3}</span> : null}
    </div>
  )
}

export function BookingsCalendarSection({ projectId, token, view, onViewChange, record, onRecordChange, onOpenContact, onOpenService, onCreateService, autoCreate = false, onAutoCreateHandled }: {
  projectId: string
  token: string
  view: 'calendar' | 'list'
  onViewChange: (view: 'calendar' | 'list') => void
  /**
   * Abrir el alta de una cita nada más llegar.
   *
   * Lo pide quien manda aquí desde otra pantalla —el vacío de Analíticas de
   * reservas— para que el botón lleve al formulario y no a otra pantalla desde
   * la que hay que volver a buscarlo.
   */
  autoCreate?: boolean
  /** Se avisa al abrirlo para que la intención no sobreviva a la navegación. */
  onAutoCreateHandled?: (() => void) | undefined
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  onOpenContact?: ((identity: string) => void) | undefined
  onOpenService?: ((offerId: string) => void) | undefined
  /**
   * Llevar al alta de un SERVICIO.
   *
   * Sin uno no hay cita posible: de él salen la duración, el horario y los
   * huecos. Quien llega aquí a crear su primera cita y no tiene ninguno, lo que
   * necesita de verdad es ese otro formulario, no un calendario vacío.
   */
  onCreateService?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const data = useBookingsData(projectId, token)
  const isNewBooking = useNewItems('bookings', data.bookings, !data.loading && !data.failed)
  const [calendarSyncOpen, setCalendarSyncOpen] = useState(false)

  /*
   * La vuelta de Google al conectar el calendario.
   *
   * El worker redirige aquí con `?google=`: se dice cómo fue, se abre la
   * ventana para que se vea la cuenta conectada y se limpia la dirección, que
   * si no repetiría el aviso en cada recarga.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get('google')
    if (!result) return
    if (result === 'connected') {
      showToast(t('calsync.toast.googleConnected'))
      setCalendarSyncOpen(true)
      data.refresh()
    } else if (result === 'cancelled') {
      showToast(t('calsync.toast.googleCancelled'))
    } else {
      showToast(t('calsync.toast.googleFailed'), 'error')
      setCalendarSyncOpen(true)
    }
    params.delete('google')
    const query = params.toString()
    window.history.replaceState(null, '', query ? `${window.location.pathname}?${query}` : window.location.pathname)
    /* Una vez y sólo una: el parámetro se borra al leerlo, así que no hay nada
       que volver a mirar aunque cambien las funciones de arriba. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [service, setService] = useState('all')
  /*
   * Todas por defecto.
   *
   * Empezó enseñando sólo las que ocupan agenda, con una casilla para sacar las
   * canceladas. Pero un calendario también sirve para mirar atrás —«¿qué pasó el
   * martes?»— y ahí una cancelación es exactamente lo que se busca. Esconderla
   * por omisión dejaba huecos sin explicación.
   */
  const [status, setStatus] = useState<'all' | Booking['status']>('all')
  /* Con quién: sólo con equipo. Con una persona todo es suyo y el filtro sobra. */
  const team = data.resources.length > 1 ? data.resources : []
  const [artist, setArtist] = useState('all')
  const artistOf = (booking: Booking) => team.find((member) => member.id === booking.resourceId)
  const [createDay, setCreateDay] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const closeCreate = () => { setCreateDay(null); setCreateOpen(false) }

  const today = todayIn(data.timezone)
  const [monthView, setMonthView] = useState<{ y: number; m: number } | null>(null)
  const current = monthView ?? { y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 }

  const openingBooking = record ?? null
  const booking = openingBooking ? data.bookings.find((row) => row.id === openingBooking) ?? null : null
  const bookingLoader = useRecordLoader(Boolean(openingBooking) && !booking && (data.loading || data.refreshing))
  const gridLoader = useRecordLoader(data.loading || data.scheduleLoading)
  /* La clave conserva la clase debajo de una cita individual. La entrada se
     deriva otra vez de los datos para reflejar cualquier cambio al regresar. */
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null)
  const [groupBusyId, setGroupBusyId] = useState<string | null>(null)

  /*
   * La intención de crear que llega de otra pantalla.
   *
   * Con servicios abre el alta de una cita; sin ninguno la reenvía al alta de un
   * servicio, que es el paso que falta de verdad. Quedarse en un calendario
   * vacío devolvería el problema a quien vino a que se lo resolvieran.
   *
   * Se avisa al padre en cuanto se atiende, para que la intención no vuelva a
   * dispararse al cambiar de mes o de vista.
   *
   * Va aquí arriba, lejos del formulario al que abre, porque debajo hay
   * returns tempranos —los cargadores— y un efecto después de uno de ellos
   * dejaría de ejecutarse a mitad de carga.
   */
  const serviceCount = data.services.length
  useEffect(() => {
    /* Se espera a que el catálogo esté cargado: con la lista todavía vacía por
       estar pidiéndose, la decisión de abajo sería siempre «no hay servicios». */
    if (!autoCreate || data.loading) return
    onAutoCreateHandled?.()
    if (serviceCount > 0) setCreateOpen(true)
    else onCreateService?.()
  }, [autoCreate, data.loading, serviceCount, onAutoCreateHandled, onCreateService])

  const saveBookingStatus = async (booking: Booking, nextStatus: Booking['status']) => {
    try {
      data.applyBooking(await updateBooking(projectId, booking.id, { status: nextStatus }, token))
      showToast(t('bookings.statusSaved'))
    } catch (error) {
      showToast(bookingError(error), 'error')
    }
  }

  const saveOpenGroupStatus = async (booking: Booking, nextStatus: Booking['status']) => {
    if (groupBusyId) return
    setGroupBusyId(booking.id)
    try {
      await saveBookingStatus(booking, nextStatus)
    } finally {
      setGroupBusyId(null)
    }
  }

  /* Calendario y lista abren la misma ficha, así que el aviso se marca leído en
     este nivel común. Antes sólo ocurría al entrar por la lista. */
  useEffect(() => {
    if (!openingBooking) return
    void markBookingChangeSeen(openingBooking, token, projectId).catch(() => { /* Se reintenta al volver a abrirla. */ })
  }, [openingBooking, token, projectId])

  /**
   * Las citas repartidas por día, en la zona del negocio.
   *
   * Se calcula una vez por cambio y no dentro del bucle de casillas: con un año
   * de historial cargado, buscar en la lista entera cuarenta y dos veces por
   * repintado es lo que convierte cambiar de mes en un tirón.
   */
  const byDay = useMemo(() => {
    const map = new Map<string, Booking[]>()
    const day = new Intl.DateTimeFormat('en-CA', {
      timeZone: data.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    })
    for (const row of data.bookings) {
      if (service !== 'all' && row.offerId !== service) continue
      if (status !== 'all' && row.status !== status) continue
      if (artist !== 'all' && row.resourceId !== artist) continue
      const key = day.format(new Date(row.startsAt))
      const list = map.get(key)
      if (list) list.push(row)
      else map.set(key, [row])
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    }
    return map
  }, [data.bookings, data.timezone, service, status, artist])
  const openGroup = useMemo(() => {
    if (!openGroupKey) return null
    const entry = groupBookings(data.bookings).find((candidate) => candidate.key === openGroupKey)
    return entry?.kind === 'group' ? entry : null
  }, [data.bookings, openGroupKey])

  const stale = Boolean(openingBooking) && !booking && !bookingLoader.visible

  /*
   * La ficha va en un diálogo, así que ya no sustituye a nada: se dibuja al
   * final, encima de la lista. La espera sí ocupa la pantalla —viene de un
   * enlace directo, cuando todavía no hay lista detrás sobre la que abrirse—.
   */
  const detail = openingBooking && !stale && booking
    ? (
      <BookingDetail
          {...(onOpenService ? { onOpenService } : {})}
          {...(() => {
            const offer = data.services.find((row) => row.id === booking.offerId)
            return {
              ...(offer?.images[0] ? { image: offer.images[0] } : {}),
              ...(offer ? { capacity: offer.booking.capacity } : {}),
              team: data.resources,
            }
          })()}
            projectId={projectId}
            token={token}
            booking={booking}
            onClose={() => onRecordChange?.(null)}
            onChange={data.applyBooking}
            onOpenContact={onOpenContact}
      />
    )
    : null

  if (openingBooking && !stale && bookingLoader.visible) return <SectionLoader loader={bookingLoader} label={t('bookings.cal.loadingBooking')} />


  if (gridLoader.visible) return <SectionLoader loader={gridLoader} label={t(view === 'calendar' ? 'bookings.cal.loadingAgenda' : 'bookings.cal.loadingList')} />

  const blanks = firstColumn(current.y, current.m)
  const total = daysInMonth(current.y, current.m)
  /*
   * Los días del mes vecino, enseñados y no en blanco.
   *
   * Una semana es una semana: el 31 de agosto y el 1 de septiembre son días
   * seguidos, y dejar uno vacío parte la fila justo en la frontera —que es
   * cuando más falta hace mirar a los dos lados, al planificar fin de mes—. Los
   * datos ya están cargados: el rango de esta pantalla cubre el historial y todo
   * el horizonte, así que esas citas ya vinieron con las demás.
   *
   * Van apagados, no rellenos de gris: un recuadro de otro color que no dice
   * nada llama la atención para nada. El tono del texto basta para decir «esto
   * es de otro mes».
   */
  const vecinos = (delta: number, cuantos: number, desdeElFinal: boolean) => {
    const total12 = current.y * 12 + current.m + delta
    const y = Math.floor(total12 / 12)
    const m = total12 - y * 12
    const dias = daysInMonth(y, m)
    return Array.from({ length: cuantos }, (_, i) => ({
      y, m, day: desdeElFinal ? dias - cuantos + i + 1 : i + 1,
    }))
  }
  const antes = vecinos(-1, blanks, true)
  /* Y se completa la última semana: una fila a medias deja la cuadrícula
     abierta por abajo, que es lo que la hace parecer una lista otra vez. */
  const despues = vecinos(1, (7 - ((blanks + total) % 7)) % 7, false)
  const shift = (delta: number) => {
    const next = current.y * 12 + current.m + delta
    setMonthView({ y: Math.floor(next / 12), m: next - Math.floor(next / 12) * 12 })
  }
  const chosenService = data.services.find((row) => row.id === service) ?? null
  /*
   * Con qué servicio se abre el alta, si es que se sabe.
   *
   * Con el filtro puesto, ése; con el filtro en «todos», NINGUNO — y entonces
   * elegirlo es el primer paso del formulario. Antes se tomaba el primero de la
   * lista, que era una elección a ciegas hecha por la pantalla en nombre de
   * quien iba a apuntar la cita.
   */
  const createService = chosenService

  const shownBookings = service === 'all'
    ? data.bookings
    : data.bookings.filter((row) => row.offerId === service)
  const clock = new Intl.DateTimeFormat(activeLanguage(), {
    timeZone: data.timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })

  /* Lo que se ve en el mes a la vista, no en todo lo cargado: el rótulo tiene
     que contar lo mismo que las casillas de debajo. */
  const shownCount = Array.from({ length: total }, (_, i) => byDay.get(keyOf(current.y, current.m, i + 1))?.length ?? 0)
    .reduce((sum, n) => sum + n, 0)

  return (
    <div className="dash-projects-view dash-pages-view dash-loaded-content-in">
      <SectionRefresh refreshing={data.refreshing} failed={data.failed} />
      <DashboardPageHeader
        title={t('bookings.cal.title')}
        subtitle={t('bookings.cal.subtitle', { timezone: data.timezone })}
        actions={(
          <>
          <HeaderRefreshButton refreshing={data.refreshing} onRefresh={data.refresh} />
          {/* Con el calendario personal: aquí es donde se echa de menos —se mira
              la semana y falta ver lo que no es una cita—. */}
          <button type="button" className="dash-secondary-btn" onClick={() => setCalendarSyncOpen(true)}>
            <IconCalendarShare size={15} />
            <span className="dash-btn-label dash-btn-label--desktop">{t('calsync.open')}</span>
            <span className="dash-btn-label dash-btn-label--compact">{t('calsync.openShort')}</span>
          </button>
          <div className="dash-bookings-view-switch" role="group" aria-label={t('bookings.cal.viewAria')}>
            <button
              type="button"
              className={view === 'calendar' ? 'is-active' : ''}
              aria-pressed={view === 'calendar'}
              onClick={() => onViewChange('calendar')}
            >
              <IconCalendarMonth size={15} />
              {t('bookings.cal.calendar')}
            </button>
            <button
              type="button"
              className={view === 'list' ? 'is-active' : ''}
              aria-pressed={view === 'list'}
              onClick={() => onViewChange('list')}
            >
              <IconListCheck size={15} />
              {t('bookings.cal.list')}
            </button>
          </div>
          {/*
            Un servicio nuevo, desde el calendario.

            El alta de un servicio con agenda vivía sólo en su catálogo, y aquí
            aparecía únicamente cuando NO había ninguno —el vacío ofrece crear
            el primero y desaparece con él—. Pero es en el calendario donde se
            descubre que falta uno: se mira la semana, se ve el hueco, y lo que
            hace falta es otro servicio que ofrecer en él. Sin este botón había
            que salir a Catálogo, encontrar la lista y volver.

            Lleva al mismo formulario que el catálogo, no a otro: `onCreateService`
            cambia de sección y lo abre allí. Un segundo alta aquí sería un
            segundo sitio donde arreglar cada regla del alta.
          */}
          {onCreateService ? (
            <button type="button" className="dash-primary-btn" onClick={onCreateService}>
              <IconPlus size={15} />
              <span className="dash-btn-label dash-btn-label--desktop">{t('bookings.svc.new')}</span>
              <span className="dash-btn-label dash-btn-label--compact">{t('bookings.svc.newShort')}</span>
            </button>
          ) : null}
          </>
        )}
      />

      {view === 'list' ? (
        <BookingsTable
          bookings={shownBookings}
          loading={false}
          failed={data.failed && data.bookings.length === 0}
          failureReason={data.failureReason}
          onReload={data.reloadBookings}
          onOpen={(bookingId) => onRecordChange?.(bookingId)}
          onOpenService={onOpenService}
          onStatusChange={saveBookingStatus}
          {...(chosenService ? { onCreate: () => setCreateOpen(true) } : {})}
          {...(onCreateService ? { onCreateService } : {})}
          {...(data.range ? { range: data.range } : {})}
          onRangeChange={data.setRange}
          rangeOn={data.rangeIsCustom}
          onRangeClear={data.clearRange}
          services={data.services}
          resources={data.resources}
          serviceFilter={service}
          onServiceFilterChange={setService}
        />
      ) : (
      <div className="dash-agenda">
        <header className="dash-agenda-bar">
          <div className="dash-agenda-month">
            <button type="button" className="dash-icon-btn" aria-label={t('bookings.cal.prevMonth')} onClick={() => shift(-1)}>
              <IconChevronLeft size={16} />
            </button>
            <strong>{monthLabel(current.y, current.m)}</strong>
            <button type="button" className="dash-icon-btn" aria-label={t('bookings.cal.nextMonth')} onClick={() => shift(1)}>
              <IconChevronRight size={16} />
            </button>
            {/* Volver a hoy: en un calendario que se puede recorrer un año en
                cada sentido, es el único atajo que hace falta. */}
            <button type="button" className="dash-secondary-btn dash-agenda-today" onClick={() => setMonthView(null)}>
              {t('bookings.cal.today')}
            </button>
          </div>

          <div className="dash-agenda-filters">
            {data.services.length > 1 ? (
              <label className="dash-toolbar-select">
                <span className="dash-sr-only">{t('bookings.filterByService')}</span>
                <select value={service} onChange={(event) => setService(event.target.value)}>
                  <option value="all">{t('bookings.allServices')}</option>
                  {data.services.map((offer) => (
                    <option key={offer.id} value={offer.id}>{offer.title}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {/* Un selector y no una casilla: los estados son cinco, no dos, y con
                «ver canceladas» no había forma de mirar sólo lo confirmado. */}
            <label className="dash-toolbar-select">
              <span className="dash-sr-only">{t('bookings.cal.filterByStatus')}</span>
              <select value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
                <option value="all">{t('bookings.cal.allStatuses')}</option>
                {(Object.keys(BOOKING_STATUS_KEY) as Booking['status'][]).map((value) => (
                  <option key={value} value={value}>{t(BOOKING_STATUS_KEY[value])}</option>
                ))}
              </select>
            </label>
            {team.length ? (
              <label className="dash-toolbar-select dash-artist-select">
                <span className="dash-sr-only">{t('bookings.filterByArtist')}</span>
                {/* La cara de la persona elegida, dentro del selector: un
                    desplegable nativo no admite fotos en sus opciones, pero sí
                    se puede poner una encima a la izquierda. */}
                {(() => {
                  const chosen = team.find((member) => member.id === artist)
                  return chosen
                    ? <ArtistFace name={chosen.name} photoUrl={chosen.photoUrl ?? null} />
                    : <span className="dash-booking-artist-face is-all" aria-hidden="true"><IconUsers size={11} /></span>
                })()}
                <select value={artist} onChange={(event) => setArtist(event.target.value)}>
                  <option value="all">{t('bookings.cal.allArtists')}</option>
                  {team.map((member) => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {/* Sólo con algo puesto: sin filtros no hay nada que quitar, y un
                botón que no hace nada es ruido en la barra. */}
            {service !== 'all' || status !== 'all' || artist !== 'all' ? (
              <button type="button" className="dash-secondary-btn dash-agenda-clear" onClick={() => { setService('all'); setStatus('all'); setArtist('all') }}>
                {t('bookings.resetFilters')}
              </button>
            ) : null}
          </div>
        </header>

        <div className="dash-agenda-week" aria-hidden="true">
          {weekdayInitials().map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
        </div>

        <div className="dash-agenda-grid">
          {antes.map((celda) => (
            <div key={`antes-${celda.y}-${celda.m}-${celda.day}`} className="dash-agenda-day is-outside">
              <div className="dash-agenda-day-head">
                <span className="dash-agenda-day-number">{celda.day}</span>
              </div>
              <MiniDayList list={byDay.get(keyOf(celda.y, celda.m, celda.day)) ?? []} onOpen={onRecordChange} />
            </div>
          ))}
          {Array.from({ length: total }, (_, index) => {
            const day = index + 1
            const key = keyOf(current.y, current.m, day)
            const list = byDay.get(key) ?? []
            return (
              <div key={key} className={`dash-agenda-day${key === today ? ' is-today' : ''}`}>
                <div className="dash-agenda-day-head">
                  <span className="dash-agenda-day-number">{day}</span>
                  <div className="dash-agenda-day-meta">
                    {list.length ? (
                      <small>{list.length === 1 ? t('bookings.cal.dayCount.one') : t('bookings.cal.dayCount.many', { count: list.length })}</small>
                    ) : null}
                  {/*
                    Añadir cita desde el día, siempre que haya algo reservable.

                    Salía sólo con el catálogo filtrado, porque el diálogo
                    recibía UN servicio y sin filtro no se sabía cuál. Ahora lo
                    elige dentro —y arranca en el filtrado si lo hay—, así que el
                    «+» puede estar en todos los días, que es donde se busca:
                    quien apunta una cita a mano mira el día, no el filtro.
                  */}
                  {data.services.length > 0 ? (
                    <button
                      type="button"
                      className="dash-agenda-add"
                      aria-label={t('bookings.cal.addOnDay', { day })}
                      onClick={() => setCreateDay(key)}
                    ><IconPlus size={13} /></button>
                  ) : null}
                  </div>
                </div>
                <div className="dash-agenda-day-list">
                  {/* Las clases, una sola pastilla. Una clase de seis llenaba el
                      día con seis pastillas de la misma hora y el mismo título, y
                      un mes con dos clases así no había quien lo leyera. */}
                  {groupBookings(list).map((entry) => {
                    const lead = entry.kind === 'group' ? entry.lead : entry.booking
                    /* Sólo la hora de inicio. El fin cabía pero no se usa: en
                       una casilla de mes se lee «¿a qué hora es?», y «10:00–12:00»
                       gasta el doble de ancho —el que necesita el nombre— para
                       responder a una pregunta que no se hace ahí. Cuánto dura
                       está en la ficha, a un clic. */
                    const time = allDayCount(lead) ? t('bookings.allDay') : clock.format(new Date(lead.startsAt))
                    const capacity = data.services.find((offer) => offer.id === lead.offerId)?.booking.capacity ?? 1
                    const isGroupService = entry.kind === 'group' || capacity > 1
                    const KindIcon = isGroupService ? IconUsers : IconUser
                    const kindLabel = t(isGroupService ? 'bookings.kind.group' : 'bookings.kind.single')

                    /* La casilla de un mes no tiene sitio para un icono más, así
                       que aquí el aviso es el borde: ámbar cuando la hora ya
                       pasó y la cita sigue sin resolverse. Mirando el mes es
                       justo lo que se busca —qué me dejé atrás— y se ve sin
                       leer ninguna de las tarjetas. */
                    const overdue = entry.kind === 'group'
                      ? entry.members.some(needsOutcome)
                      : needsOutcome(entry.booking)
                    const overdueNote = overdue ? ` · ${t(OVERDUE_HINT_KEY)}` : ''

                    if (entry.kind === 'one') {
                      const customer = lead.customerName || t('bookings.noName')
                      const person = artistOf(lead)
                      const description = `${time} · ${customer} · ${lead.offerTitle}${person ? ` · ${t('bookings.withArtist', { name: person.name })}` : ''} · ${kindLabel} · ${t(BOOKING_STATUS_KEY[lead.status])}${overdueNote}`
                      const StatusIcon = BOOKING_STATUS_ICON[lead.status]
                      return (
                        <button
                          key={lead.id}
                          type="button"
                          className={`dash-agenda-chip ${BOOKING_STATUS_TONE[lead.status]}${overdue ? ' is-overdue' : ''}`}
                          title={description}
                          aria-label={description}
                          onClick={() => onRecordChange?.(lead.id)}
                        >
                          <strong className="dash-agenda-chip-time">{time}</strong>
                          {/* El estado, en un icono junto al nombre en vez de en
                              una línea escrita. Un punto de color decía «pasa
                              algo» pero no qué; una palomita o una tacha se leen
                              sin haber aprendido el código de colores. El texto
                              sigue en el `title` y en el `aria-label`, así que
                              quien usa lector de pantalla no pierde nada. */}
                          <span className="dash-agenda-chip-customer">
                            {isNewBooking(lead) ? <NewItemDot /> : null}
                            <StatusIcon className="dash-agenda-chip-mark" size={11} aria-hidden="true" />
                            {/* En su propio `span`: un nodo de texto suelto dentro
                                de un flex es una caja anónima, y `text-overflow`
                                no llega a ella —el nombre se salía en vez de
                                acortarse—. */}
                            <span>{customer}</span>
                          </span>
                          <small className="dash-agenda-chip-service"><KindIcon aria-hidden="true" /><span>{lead.offerTitle}</span></small>
                          {/* Con quién: la cara y el nombre, como en la lista. */}
                          {person ? (
                            <small className="dash-agenda-chip-artist">
                              <ArtistFace name={person.name} photoUrl={person.photoUrl ?? null} />
                              <span>{person.name}</span>
                            </small>
                          ) : null}
                        </button>
                      )
                    }

                    const groupPerson = artistOf(lead)
                    const description = `${time} · ${lead.offerTitle}${groupPerson ? ` · ${t('bookings.withArtist', { name: groupPerson.name })}` : ''} · ${kindLabel} · ${peopleLabel(entry.members.length, t)}${overdueNote}`
                    return (
                      <div className="dash-agenda-group" key={entry.key}>
                        {/* Una clase abre su ficha conjunta, igual que en la
                            lista. Desde ahí se puede entrar a cada cita. */}
                        <button
                          type="button"
                          className={`dash-agenda-chip is-group${overdue ? ' is-overdue' : ''}`}
                          title={description}
                          aria-label={description}
                          aria-haspopup="dialog"
                          onClick={() => setOpenGroupKey(entry.key)}
                        >
                          <strong className="dash-agenda-chip-time">{time}</strong>
                          <span className="dash-agenda-chip-customer">{entry.members.some(isNewBooking) ? <NewItemDot /> : null}<span>{lead.offerTitle || t('bookings.group.fallbackTitle')}</span></span>
                          <small className="dash-agenda-chip-service"><KindIcon aria-hidden="true" /><span>{peopleLabel(entry.members.length, t)}</span></small>
                          {groupPerson ? (
                            <small className="dash-agenda-chip-artist">
                              <ArtistFace name={groupPerson.name} photoUrl={groupPerson.photoUrl ?? null} />
                              <span>{groupPerson.name}</span>
                            </small>
                          ) : null}
                          <em className="dash-agenda-chip-more">{t('bookings.cal.viewClass')}</em>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
          {despues.map((celda) => (
            <div key={`despues-${celda.y}-${celda.m}-${celda.day}`} className="dash-agenda-day is-outside">
              <div className="dash-agenda-day-head">
                <span className="dash-agenda-day-number">{celda.day}</span>
              </div>
              <MiniDayList list={byDay.get(keyOf(celda.y, celda.m, celda.day)) ?? []} onOpen={onRecordChange} />
            </div>
          ))}
        </div>

        {/*
          El vacío grande sólo cuando falta el servicio, que es lo único que
          impide que el calendario funcione.

          Había otro para «todavía no hay ninguna cita» que ofrecía apuntar la
          primera a mano. Pero con servicios publicados un mes sin citas no es un
          problema que resolver ni un paso que falte: es un mes tranquilo, y una
          tarjeta de treinta píxeles de alto invitando a inventarse una cita cada
          vez que se mira un mes vacío es ruido. Ahí basta el renglón de abajo.
          Apuntar una a mano sigue estando en el «+» de cada día y en la barra de
          la lista, que es donde se busca cuando de verdad hace falta.
        */}
        {shownCount === 0 ? (
          data.services.length === 0 && onCreateService ? (
            /* Sin servicios el calendario no puede llenarse de ninguna manera:
               el paso que falta no es una cita, es el servicio del que colgarla. */
            <div className="dash-placeholder dash-agenda-placeholder">
              <IconCalendarPlus size={34} aria-hidden="true" />
              <strong>{t('bookings.cal.noServicesTitle')}</strong>
              <span>{t('bookings.cal.noServicesBody')}</span>
              <button type="button" className="dash-primary-btn" onClick={onCreateService}>
                <IconPlus size={15} aria-hidden="true" /> {t('bookings.cal.noServicesCta')}
              </button>
            </div>
          ) : (
            <p className="dash-agenda-empty">
              {data.services.length === 0
                ? t('bookings.cal.noServices')
                : service !== 'all' || status !== 'all' || artist !== 'all'
                  /* Vacío por el filtro, no porque el mes esté tranquilo: se dice, y
                     se ofrece quitarlo ahí mismo. */
                  ? (
                    <>
                      {t('bookings.cal.noneFiltered')}{' '}
                      <button type="button" className="dash-text-link" onClick={() => { setService('all'); setStatus('all'); setArtist('all') }}>
                        {t('bookings.resetFilters')}
                      </button>
                    </>
                  )
                  : t('bookings.cal.noneThisMonth')}
            </p>
          )
        ) : null}
      </div>
      )}

      {openGroup ? (
        <GroupBookingDialog
          {...(() => {
            const shot = data.services.find((row) => row.id === openGroup.lead.offerId)?.images[0]
            return shot ? { image: shot } : {}
          })()}
          entry={openGroup}
          busyId={groupBusyId}
          onClose={() => setOpenGroupKey(null)}
          onOpen={(bookingId) => onRecordChange?.(bookingId)}
          onStatusChange={saveOpenGroupStatus}
        />
      ) : null}

      {/* Un solo alta con dos maneras de llegar: el «+» de un día —que ya trae
          la fecha— y el botón del vacío. Eran dos montajes idénticos salvo por
          `initialDay`, y cualquier arreglo había que hacerlo dos veces. */}
      {(createDay || createOpen) && data.services.length > 0 ? (
        <NewBookingDialog
          projectId={projectId}
          token={token}
          {...(createService ? { service: createService } : {})}
          services={data.services}
          timezone={data.timezone}
          resources={data.resources}
          {...(createDay ? { initialDay: createDay } : {})}
          onClose={closeCreate}
          onCreated={() => { closeCreate(); data.reloadBookings() }}
        />
      ) : null}

      {detail}
      {calendarSyncOpen ? (
        <CalendarSyncDialog
          projectId={projectId}
          token={token}
          resources={data.resources}
          onClose={() => setCalendarSyncOpen(false)}
        />
      ) : null}
    </div>
  )
}
